import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail, ShieldAlert, ToggleRight } from "lucide-react";
import { api } from "@/lib/api";
import { useContextoActivo } from "@/hooks/useTelecomAccess";

/**
 * Panel mostrado cuando el cliente no puede entrar a un módulo de sector.
 *
 * Ajuste 2026-07-12 (autogestión de sectores): si el bloqueo es porque el
 * sector no está habilitado para la empresa del contexto activo, se ofrece
 * habilitarlo al vuelo (botón «Habilitar sector») — el cliente administra sus
 * propios sectores para no obstaculizar la operación. El caso sin contexto
 * (ninguna empresa vinculada) conserva el aviso hacia Calidad.
 *
 * D-04 (F4, requerimiento RL 2026-07-15): la autogestión es exclusiva del
 * REPRESENTANTE DIRECTO; a los usuarios autorizados y vinculados vía
 * tramitador se les indica pedirlo a su representante o a Calidad (el backend
 * también lo rechaza con 403 facultad-insuficiente).
 *
 * Corrección 2026-08-06: el aviso señalaba al lugar equivocado. Los sectores se
 * resuelven en DOS capas (ver kernel.v_contexto_actuacion):
 *   - REPRESENTANTE_DIRECTO  -> kernel.registro_legal_sector (los del RL).
 *   - Alcance acotado por carta poder (INTERNO_TITULAR, CONSULTA, GESTION,
 *     SEGUIMIENTO, FIRMA) -> kernel.asociacion_legal_sector.
 * Para el segundo grupo el sector puede estar perfectamente habilitado en el RL
 * y aun así estar bloqueado, así que decir «no está habilitado para {empresa}»
 * es falso y mandaba al usuario a pedirle a su representante un cambio que NO
 * lo desbloquea: habilitar un sector en el RL solo se propaga a asociaciones
 * INTEGRAL, y ese valor ni siquiera lo admite el CHECK de
 * kernel.asociacion_legal. Ampliar el alcance es cosa de Calidad
 * (PATCH /api/usuarios-autorizados/{asociacionId}), RN-05 default-deny.
 */
export function SectorNoHabilitadoPanel({
  sectorNombre,
  titularRazonSocial,
  sinContexto,
  sectorCodigo,
}: {
  sectorNombre: string;
  titularRazonSocial: string | null;
  sinContexto: boolean;
  /** Código del sector (p. ej. "TELECOM"); habilita el botón de autogestión. */
  sectorCodigo?: string;
}) {
  const qc = useQueryClient();
  const { contexto } = useContextoActivo();
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const esRepresentanteDirecto = contexto?.tipoAutorizacion === "REPRESENTANTE_DIRECTO";
  const puedeHabilitar = !!sectorCodigo && !sinContexto && !!contexto && esRepresentanteDirecto;
  /**
   * El bloqueo viene del alcance de la carta poder, no de los sectores de la
   * empresa: el sector puede estar habilitado en el RL y seguir bloqueado.
   */
  const porAlcanceDeCartaPoder = !sinContexto && !!contexto && !esRepresentanteDirecto;

  const habilitar = async () => {
    if (!contexto || !sectorCodigo) return;
    setGuardando(true);
    setError(null);
    try {
      const codigos = Array.from(new Set([...(contexto.sectoresCodigos ?? []), sectorCodigo]));
      await api.put(`/api/registros-legales/${contexto.titularId}/sectores`, {
        sectoresCodigos: codigos,
      });
      // El hook de acceso recalcula con los contextos frescos y desbloquea la página.
      await qc.invalidateQueries({ queryKey: ["contextos-mios"] });
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ?? e?.response?.data?.message ??
          "No se pudo habilitar el sector. Intenta de nuevo o contacta a Calidad.",
      );
    } finally {
      // Si la página no se desbloqueó (p. ej. el contexto no reflejó aún el
      // cambio), no dejar el botón girando para siempre.
      setGuardando(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl rounded-lg border border-estado-warn/25 bg-estado-warn-bg p-6">
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-6 w-6 shrink-0 text-estado-warn" />
        <div className="flex-1">
          <h2 className="text-base font-semibold text-estado-warn">
            {sinContexto
              ? "Sin Registro Legal asociado"
              : porAlcanceDeCartaPoder
                ? `Tu autorización no incluye el sector ${sectorNombre}`
                : `Sector ${sectorNombre} no habilitado`}
          </h2>
          {sinContexto ? (
            <p className="mt-2 text-sm text-estado-warn">
              No tienes ningún Registro Legal activo asociado. Si crees que es un error,
              contacta al área de Calidad para revisar tus permisos.
            </p>
          ) : porAlcanceDeCartaPoder ? (
            <p className="mt-2 text-sm text-estado-warn">
              Tu autorización para actuar por{" "}
              <span className="font-medium">{titularRazonSocial ?? "este Registro Legal"}</span>{" "}
              no incluye el sector de <span className="font-medium">{sectorNombre}</span>. El
              alcance por sector lo define la carta poder, así que habilitarlo en el Registro
              Legal no lo desbloquea: pide a Calidad de NPMX que amplíe tu autorización.
            </p>
          ) : (
            <p className="mt-2 text-sm text-estado-warn">
              El sector de <span className="font-medium">{sectorNombre}</span> no se encuentra habilitado para
              {" "}
              <span className="font-medium">{titularRazonSocial ?? "este Registro Legal"}</span>.
              {puedeHabilitar
                ? " Puedes habilitarlo ahora mismo si lo necesitas para operar."
                : null}
            </p>
          )}
          {error && <p className="mt-2 text-sm font-medium text-estado-error">{error}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {puedeHabilitar && (
              <button
                type="button"
                onClick={habilitar}
                disabled={guardando}
                className="inline-flex items-center gap-1.5 rounded bg-estado-warn px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
              >
                {guardando
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <ToggleRight className="h-4 w-4" />}
                Habilitar sector {sectorNombre}
              </button>
            )}
            <a
              href="mailto:calidad@npmx.org"
              className="inline-flex items-center gap-1.5 rounded border border-estado-warn/25 bg-surface px-3 py-1.5 text-sm font-medium text-estado-warn hover:bg-estado-warn-bg"
            >
              <Mail className="h-4 w-4" /> calidad@npmx.org
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
